import React, { useEffect, useState } from 'react';
import { rosterApi } from '../services/api';
import TrackCompanyDialog from './TrackCompanyDialog';

const number = (value) => typeof value === 'number' && Number.isFinite(value);
const pct = (value) => number(value) ? `${value >= 0 ? '+' : ''}${value.toFixed(1)}%` : 'n/a';
export const pulseBand = (value, status) => status ? status === 'PRE-IPO' ? 'pulse-missing' : status === 'ACQUIRED' ? 'pulse-acquired' : 'pulse-retired'
  : !number(value) ? 'pulse-missing' : Math.abs(value) < 1 ? 'pulse-flat'
    : `${value > 0 ? 'pulse-up' : 'pulse-down'}${Math.abs(value) >= 10 ? '-large' : Math.abs(value) >= 5 ? '-medium' : ''}`;

const sortCards = (sections) => [...sections].sort((a, b) => {
  if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1;
  const av = a.stats?.avg_change_pct, bv = b.stats?.avg_change_pct;
  return (number(bv) ? bv : -Infinity) - (number(av) ? av : -Infinity) || String(a.title).localeCompare(String(b.title));
});

const MarketPulse = ({ doc, onOpenCompany, canAdminRefresh = false, onRefresh, publicMode = false, hideIncludedLabel = false }) => {
  const [trackCompany, setTrackCompany] = useState(null);
  const [added, setAdded] = useState([]);
  const [capability, setCapability] = useState(false);
  const [execution, setExecution] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    if (!canAdminRefresh) return undefined;
    let active = true;
    rosterApi.pulseRefreshCapability().then((result) => { if (active) setCapability(result.can_refresh); })
      .catch((err) => { if (active) setError(`Could not check QA refresh availability: ${err.message}`); });
    return () => { active = false; };
  }, [canAdminRefresh]);
  useEffect(() => {
    if (!execution) return undefined;
    let active = true;
    let timer;
    const poll = async () => {
      try {
        const result = await rosterApi.pulseRefreshStatus(execution);
        if (!active) return;
        if (result.status === 'RUNNING') {
          setMessage('Shared refresh is running. This page will reload when publication completes.');
          timer = setTimeout(poll, 5000);
        } else {
          setBusy(false);
          setExecution('');
          if (result.status === 'SUCCEEDED') {
            setMessage('Shared Market Pulse refresh completed.');
            onRefresh?.();
          } else {
            setMessage('');
            setError(`Shared refresh ${result.status.toLowerCase()}: ${result.error || 'Check the workflow execution for details.'}`);
          }
        }
      } catch (err) {
        if (active) { setBusy(false); setExecution(''); setMessage(''); setError(`Could not check refresh status: ${err.message}. The job may still be running.`); }
      }
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [execution, onRefresh]);
  const start = async () => {
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await rosterApi.refreshPulse();
      setExecution(result.execution);
      setMessage(result.already_running ? 'An existing shared refresh is running.' : 'Shared refresh started.');
    } catch (err) { setBusy(false); setError(`Could not start shared refresh: ${err.message}`); }
  };
  const sections = doc.sections || [];
  const tracking = new Set([...(doc.access?.tracked ?? doc.access?.held ?? []), ...added].map((ticker) => ticker.toUpperCase()));
  const included = new Set((doc.access?.included || []).map((ticker) => ticker.toUpperCase()));
  const firstlookOpen = new Set([...(doc.access?.firstlook_open || []), ...tracking, ...included].map((ticker) => ticker.toUpperCase()));
  const openFirstLook = (ticker) => {
    if (onOpenCompany) onOpenCompany(ticker, 'firstlook');
    else window.location.assign(`/company/${encodeURIComponent(ticker)}?product=firstlook`);
  };
  const rows = sections.flatMap((section) => section.rows || []);
  const groups = new Map();
  sections.forEach((section) => {
    const name = section.banner || 'Diversified Disruptors';
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(section);
  });
  const priority = (name) => ({ 'AI DATACENTER': 0, 'DIVERSIFIED DISRUPTORS': 1, HEALTHCARE: 2, 'INTERNATIONAL / UNCLASSIFIED': 3 }[name.toUpperCase()] ?? 1);
  const dates = [...new Set(rows.map((row) => row.quote_date).filter(Boolean))].sort();
  const unavailable = rows.filter((row) => !number(row.change_pct) && !row.trading_status).length;
  return <div className="market-pulse">
    <section className="earnings-intro">
      <p><strong>{rows.length}</strong> visible companies · {sections.length} business groups · Daily percentage change</p>
      <p className="muted">Quote snapshot dates (Eastern): {dates.length ? dates.length === 1 ? dates[0] : `${dates[0]} to ${dates[dates.length - 1]}` : 'Unavailable'}. This is an hourly snapshot, not a live stream. Averages are equal-weighted across available, non-retired quotes; missing or stale quotes are excluded.</p>
      <p className="pulse-key"><span className="pulse-up">Up 1%+</span><span className="pulse-down">Down 1%+</span><span className="pulse-up-medium">Up 5%+</span><span className="pulse-down-medium">Down 5%+</span><span className="pulse-up-large">Up 10%+</span><span className="pulse-down-large">Down 10%+</span></p>
      {unavailable > 0 && <div className="error-box" role="status">{unavailable} visible companies have missing or stale quotes. n/a does not mean zero change.</div>}
      {(doc.summary?.warnings || []).map((warning) => <div className="error-box" role="status" key={warning}>{warning}</div>)}
      {canAdminRefresh && capability && <button className="secondary-button" disabled={busy} onClick={start}>{busy ? 'Refreshing shared data...' : 'Refresh Market Pulse'}</button>}
      {message && <p role="status">{message}</p>}
      {error && <div className="error-box" role="alert">{error}</div>}
    </section>
    {!sections.length && <section className="panel empty-state">No Market Pulse groups are available yet. A shared quote refresh and publication are required.</section>}
    {[...groups].sort(([a], [b]) => priority(a) - priority(b) || a.localeCompare(b)).map(([name, cards]) => {
      const quoted = cards.flatMap((card) => card.rows || []).filter((row) => number(row.change_pct) && !row.trading_status);
      const allVisible = cards.every((card) => Array.isArray(card.rows));
      return <section className="pulse-banner" key={name}>
        <div className="pulse-banner-heading"><h2>{name}</h2>{allVisible && <span>{pct(quoted.length ? quoted.reduce((sum, row) => sum + row.change_pct, 0) / quoted.length : null)} · avg of {quoted.length}</span>}</div>
        <div className="pulse-card-columns">{sortCards(cards).map((card) => {
          const members = [...(card.rows || [])].sort((a, b) => (number(b.change_pct) ? b.change_pct : -Infinity) - (number(a.change_pct) ? a.change_pct : -Infinity) || String(a.company || a.ticker).localeCompare(String(b.company || b.ticker)));
          return <article className="pulse-group" key={card.id || card.group}>
            <div className="pulse-group-heading"><h3>{card.title || card.group}</h3><span>{pct(card.stats?.avg_change_pct)} · {card.stats?.quoted != null || Array.isArray(card.rows) ? `avg of ${card.stats?.quoted ?? members.filter((row) => number(row.change_pct)).length}` : 'group average'}</span></div>
            {!members.length ? <p className="muted">{card.stats?.members || 0} members · Your plan shows group summaries. Track a company or compare plans to open eligible peer groups.</p> : members.map((row) => <div className={`pulse-company ${pulseBand(row.change_pct, row.trading_status)}`} key={row.ticker}>
              <a href={firstlookOpen.has(row.ticker.toUpperCase()) ? `/company/${encodeURIComponent(row.ticker)}?product=firstlook` : '/plans'} onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                if (!firstlookOpen.has(row.ticker.toUpperCase())) { event.preventDefault(); setTrackCompany(row); }
                else if (onOpenCompany) { event.preventDefault(); openFirstLook(row.ticker); }
              }}><strong>{row.ticker}</strong><span>{row.company || row.ticker}{tracking.has(row.ticker.toUpperCase()) && <em className="pulse-owned">Tracking</em>}{!hideIncludedLabel && included.has(row.ticker.toUpperCase()) && <em className="pulse-owned">Included</em>}{row.trading_status && <em className="pulse-owned">{row.trading_status}</em>}</span></a>
              <strong className="pulse-change" title={row.trading_status === 'PRE-IPO' ? 'Pre-IPO; stock not yet trading' : row.quote_stale ? 'Quote snapshot is older than 24 hours or has no valid fetch date' : row.quote_missing ? 'Quote unavailable' : undefined}>{row.trading_status ? '—' : pct(row.change_pct)}</strong>
            </div>)}
          </article>;
        })}</div>
      </section>;
    })}
    {trackCompany && <TrackCompanyDialog key={trackCompany.ticker} company={trackCompany} publicMode={publicMode} onClose={() => setTrackCompany(null)} onTracked={(ticker) => {
      setAdded((current) => [...current, ticker]); setTrackCompany(null); openFirstLook(ticker);
    }} onOpened={(ticker) => { setTrackCompany(null); openFirstLook(ticker); }} />}
  </div>;
};

export default MarketPulse;
